import {Button} from "@zendeskgarden/react-buttons";
import {Dropdown, Field, Item, Label, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input as FormInput, Label as FormLabel} from "@zendeskgarden/react-forms";
import cn from "classnames";
import {memo, ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import {
    AllPropertyForecastLocksQuery,
    OperationalDriverAssumptionsUpdateInput,
    useDeleteOperationalDriversMutation,
    useLoadOperationalDriversQuery,
    useSetOperationalDriversStatusMutation,
    useUpdateOperationalDriverAssumptionsMutation,
    VersionType
} from "../../../__generated__/generated_types";
import {OpBulkDriverNoteModal} from "../../../components/driver-note/BulkDriverNoteModal";
import {OpDriverNoteModal} from "../../../components/driver-note/DriverNoteModal";
import VZSaveStatus from "../../../components/vz-ui/vz-save-status/VZSaveStatus";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {round, roundArray} from "../../../utils/math";
import {getMonths} from "../../../utils/months-helper";
import {parseNumber} from "../../../utils/string-helpers";
import {
    CopyOperationalDriverAssumptionsModal,
    CopyOperationalDriverSourceDriver
} from "./CopyOperationalDriverAssumptionsModal";
import {CreateOperationalDriverTooltipModal} from "./CreateOperationalDriverTooltipModal";
import {DeleteLineItemsModal} from "./DeleteLineItemsModal";
import {addToArray, cellTypeToNumber, divideArray, filterSelectedLineItems, toLockLookup, GroupedOperationalDriver, getStatusText} from "./logic";
import OpDriverConfigModal, {OpDriverConfigItem} from "./OpDriverConfigModal";
import {SetStatusModal} from "./SetStatusModal";
import {
    CompoundSimpleHotRow,
    SimpleHoTCellStyle,
    SimpleHoTChange,
    SimpleHoTRow,
    SimpleHoTWrapper
} from "./SimpleHoTWrapper";
import {SimpleHoTWrapperControls} from "./SimpleHoTWrapperControls";
import * as css from "./styles/OperationalTab.module.scss";
import * as controls from "./styles/SimpleHoTWrapper.module.scss";

export interface OperationalTabProps {
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    operationalMetricTypes: {id: string, name: string}[];
    locks: AllPropertyForecastLocksQuery | undefined;
    locksLoading: boolean;
}

const COL_TO_MONTH: Record<string, number> = {
    "values.0": 0,
    "values.1": 1,
    "values.2": 2,
    "values.3": 3,
    "values.4": 4,
    "values.5": 5,
    "values.6": 6,
    "values.7": 7,
    "values.8": 8,
    "values.9": 9,
    "values.10": 10,
    "values.11": 11
};

enum GroupByOptions {
    OpItem,
    Property,
    Account
}

const GroupByLabels = {
    [GroupByOptions.OpItem]: "Operational Item",
    [GroupByOptions.Property]: "Property",
    [GroupByOptions.Account]: "Account"
};

export function OperationalTab(props: OperationalTabProps): ReactElement {
    const {data, loading, refetch} = useLoadOperationalDriversQuery({
        fetchPolicy: "no-cache",
        variables: {
            budgetYear: props.budgetYear,
            versionType: props.versionType
        },
    });
    const [updateOperationalDriver, {loading: updateOperationalDriverLoading}] = useUpdateOperationalDriverAssumptionsMutation({
        notifyOnNetworkStatusChange: true
    });
    const [setOperationalDriversStatus, {loading: setStatusLoading}] = useSetOperationalDriversStatusMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [deleteOperationalDrivers, {loading: deleteOperationalDriversLoading}] = useDeleteOperationalDriversMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [collectedChanges, setCollectedChanges] = useState<SimpleHoTChange[]>([]);

    const addButtonRef = useRef<HTMLButtonElement>(null);
    const [addTooltipVisible, setAddTooltipVisible] = useState<boolean>(false);
    const [setStatusModal, setSetStatusModal] = useState<{
        driverIds: string[];
        itemName: string[];
        initialLocked: boolean;
        initialValuesLocked: boolean;
        initialConfigLocked: boolean;
    } | null>(null);
    const [deleteModal, setDeleteModal] = useState<{driverIds: string[]} | null>(null);
    const [copyModal, setCopyModal] = useState<{
        sourceDriver: CopyOperationalDriverSourceDriver;
        destinationProperties: {id: string; name: string, disabled?: boolean}[];
        defaultSelectedProperties: string[],
        accounts: {id: string; label: string}[];
    } | null>(null);
    const [configModal, setConfigModal] = useState<OpDriverConfigItem[] | null>(null);

    const {properties} = useProperties();
    const {accounts} = useChartOfAccounts();

    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [groupBy, setGroupBy] = useState<GroupByOptions>(GroupByOptions.OpItem);
    const [groupByFilter, setGroupByFilter] = useState<string | undefined>(undefined);
    const [tmpGroupByFilter, setTmpGroupByFilter] = useState<string | undefined>(undefined);

    const [editingNoteLineId, setEditingNoteLineId] = useState<string | null>(null);
    const [editingNoteLineName, setEditingNoteLineName] = useState<string | null>(null);
    const [bulkEditingNoteLineIds, setBulkEditingNoteLineIds] = useState<string[] | null>(null);
    const [bulkEditingNoteLineNames, setBulkEditingNoteLineNames] = useState<string[] | null>(null);

    useEffect(() => {
        const to = setTimeout(() => {
            setGroupByFilter(tmpGroupByFilter);
        }, 500);
        return () => clearTimeout(to);
    }, [tmpGroupByFilter]);

    const rows = useMemo<GroupedOperationalDriver[]>(() => {
        if (loading || !data?.loadOperationalDrivers || props.locksLoading) {
            return [];
        }

        const opMetricsMap = props.operationalMetricTypes.toIdMap(m => m.id);
        const accountsMap = accounts.toIdMap(a => a.id);
        const propertyMap = properties?.toIdMap(p => p.id) ?? {};

        return data.loadOperationalDrivers.map(d => {
            const property = propertyMap[d.propertyId];
            const account = accountsMap[d.destinationAccountId];
            const opMetric = opMetricsMap[d.operationalMetricTypeId];
            const secondaryOpMetric = d.secondaryOperationalMetricTypeId ? opMetricsMap[d.secondaryOperationalMetricTypeId] : null;

            return {
                driverId: d.id,
                propertyId: d.propertyId,
                destinationAccountId: d.destinationAccountId,
                name: d.name,
                locked: d.locked,
                valuesLocked: d.valuesLocked,
                configLocked: d.configLocked,
                amounts: d.amounts,
                percents: d.percents,
                operationalMetricTypeId: d.operationalMetricTypeId,
                operationalMetric: opMetric?.name ?? d.operationalMetricTypeId,
                secondaryOperationalMetricTypeId: d.secondaryOperationalMetricTypeId ?? null,
                secondaryOperationalMetric: secondaryOpMetric?.name ?? null,
                propertyName: property?.name ?? d.propertyId,
                accountLabel: account ? `${account.accountNumber} - ${account.name}` : d.destinationAccountId,
                delayRecognition: d.delayRecognition ?? 0,
                cumSum: d.cumSum ?? null,
                nonProrated: d.nonProrated ?? false,
                minValue: parseNumber(d.minValue),
                maxValue: parseNumber(d.maxValue),
                year: d.year,
                versionType: d.versionType,
            };
        });
    }, [data, loading, properties, accounts, props.operationalMetricTypes, props.locksLoading]);

    const locksLookup = useMemo<Set<string>>(() => {
        return toLockLookup(props.versionType, props.locks);
    }, [props.versionType, props.locks]);

    const groupedRows = useMemo(() => {
        let sortedRows = rows;

        if(groupBy === GroupByOptions.OpItem) {
            sortedRows = rows.sortBy("name", "propertyName", "accountLabel");
        } else if(groupBy === GroupByOptions.Property) {
            sortedRows = rows.sortBy("propertyName", "name", "accountLabel");
        } else {
            sortedRows = rows.sortBy("accountLabel", "name", "propertyName");
        }

        const grouped = sortedRows.groupBy(d => {
            if(groupBy === GroupByOptions.OpItem) {
                return d.name;
            } else if(groupBy === GroupByOptions.Property) {
                return d.propertyName;
            } else {
                return d.accountLabel;
            }
        });

        if(groupByFilter) {
            const filtered: typeof grouped = {};
            for(const [key, val] of Object.entries(grouped)) {
                if(key.toLowerCase().includes(groupByFilter.toLowerCase())) {
                    filtered[key] = val;
                }
            }
            return filtered;
        }
        return grouped;
    }, [rows, groupBy, groupByFilter]);

    const [simpleHotRows, setSimpleHotRows] = useState<SimpleHoTRow[]>([]);

    useEffect(() => {
        const keys = Object.keys(groupedRows).sort();
        const hotRows: CompoundSimpleHotRow[] = [];
        for(const key of keys) {
            const val = groupedRows[key];
            const amountsTotal: number[] = new Array(12).fill(0);
            if(val) {
                const childRows: CompoundSimpleHotRow[] = [];
                let childCount = 0 ;
                let lockedCount = 0;
                let valuesLockedCount = 0;
                let configLockedCount = 0;
                for(const child of val) {
                    let childAmounts = child.amounts.map(v => v ?? 0);
                    let amountsType = "$ Amounts";
                    if(child.secondaryOperationalMetricTypeId) {
                        amountsType = `$ Amounts N/A`;
                        childAmounts = new Array(12).fill(0);
                    }
                    const childPercents = child.percents.map(v => v ?? 0);
                    addToArray(amountsTotal, childAmounts);
                    let status = [
                        child.locked ? "Edit-Only" : undefined,
                        child.valuesLocked ? "Read-Only" : undefined,
                        child.configLocked ? "Configurable" : undefined,
                    ].filter(Boolean).join(", ");
                    if(status === "") {
                        status = "Full Access";
                    }
                    if(child.locked) {
                        lockedCount++;
                    }
                    if(child.valuesLocked) {
                        valuesLockedCount++;
                    }
                    if(child.configLocked) {
                        configLockedCount++;
                    }
                    childRows.push({
                        isCompound: true,
                        values: [
                            {
                                label: key,
                                property: child.propertyName,
                                account: child.accountLabel,
                                opItem: child.name,
                                status: status,
                                values: childAmounts,
                                type: amountsType,
                                valuesType: "amounts",
                            },
                            {
                                label: key,
                                property: child.propertyName,
                                account: child.accountLabel,
                                opItem: child.name,
                                status: status,
                                values: childPercents,
                                type: `% of ${child.operationalMetric}`,
                                valuesType: "percents"
                            }
                        ],
                        verticalColumnMerges: [0, 1, 2, 3],
                        extraData: {
                            opItem: child,
                            type: "amounts",
                            amountsNotAvailable: child.secondaryOperationalMetricTypeId ? true : false
                        }
                    });
                    childCount++;
                }

                let status: string | string[] = [];

                if (lockedCount > 0) {
                    status.push(lockedCount === val.length ? "Edit-Only" : "Partially Edit-Only");
                }

                if (valuesLockedCount > 0) {
                    status.push(valuesLockedCount === val.length ? "Read-Only" : "Partially Read-Only");
                }

                if (configLockedCount > 0) {
                    status.push(configLockedCount === val.length ? "Configurable" : "Partially Configurable");
                }

                status = status.join(", ");

                if (status === "") {
                    status = "Full Access";
                }

                hotRows.push({
                    isCompound: true,
                    verticalColumnMerges: [],
                    values: [{
                        label: key,
                        property: null,
                        account: null,
                        opItem: null,
                        status: status,
                        type: "Avg $ Amount",
                        values: roundArray(divideArray(amountsTotal, childCount), 2)
                    }],
                    children: childRows,
                    summaryUpdate: change => {
                        if(change.compoundRowValuesIndex !== 0) {
                            return undefined;
                        }

                        const colIdx = COL_TO_MONTH[change.col];
                        if(colIdx === undefined) {
                            return undefined;
                        }

                        const child = change.parent as CompoundSimpleHotRow;
                        const childValues = (child.values[0] as {values: number[]}).values;
                        if(colIdx < childValues.length) {
                            const newVal = cellTypeToNumber(change.newValue);
                            if(newVal !== null) {
                                childValues[colIdx] = newVal;
                            }
                        }

                        // This may be to slow. We'll have to see.
                        const row = change.parent as CompoundSimpleHotRow;
                        if(row.children && row.children.length > 0) {
                            let total = 0;
                            for(const child of row.children) {
                                const childValues = (child.values[0] as { values: number[] }).values;
                                const val = childValues[colIdx];
                                if(val !== undefined) {
                                    const local = cellTypeToNumber(val);
                                    if(local !== null) {
                                        total += local;
                                    }
                                }
                            }
                            return round(total / row.children.length, 2);
                        }
                        return undefined;
                    }
                });
            }
        }
        setSimpleHotRows(hotRows);
    }, [groupedRows]);

    useEffect(() => {
        if(updateOperationalDriverLoading || collectedChanges.length === 0) {
            return;
        }

        // This takes care of having more than one change per month.
        // The value of the most recent change is used.
        const byId: Record<string, OperationalDriverAssumptionsUpdateInput> = {};
        for(const change of collectedChanges) {
            if(!change.row.isCompound || change.compoundRowValuesIndex === undefined) {
                continue;
            }
            const values = change.row.values[change.compoundRowValuesIndex];
            if(!values) {
                continue;
            }

            const opItem = change.row.extraData?.["opItem"] as GroupedOperationalDriver;
            const type = values["valuesType"] as "amounts" | "percents" | undefined;
            if(type && opItem && typeof change.col === "string") {
                const driverId = opItem.driverId;
                let update = byId[driverId];
                const month = COL_TO_MONTH[change.col];
                if(month !== undefined) {
                    if(!update) {
                        update = {
                            year: props.versionType === VersionType.Budget ? props.budgetYear : props.budgetYear - 1,
                            versionType: props.versionType,
                            destinationAccountId: opItem.destinationAccountId,
                            propertyId: opItem.propertyId,
                            sourceMetricId: opItem.operationalMetricTypeId,
                            secondarySourceMetricId: opItem.secondaryOperationalMetricTypeId,
                            name: opItem.name
                        };
                        byId[driverId] = update;
                    }

                    if(type === "percents") {
                        const val= typeof change.newValue === "string" ? parseFloat(change.newValue) : change.newValue;
                        switch(month) {
                            case 0:
                                update.percentMonth0 = val;
                                break;
                            case 1:
                                update.percentMonth1 = val;
                                break;
                            case 2:
                                update.percentMonth2 = val;
                                break;
                            case 3:
                                update.percentMonth3 = val;
                                break;
                            case 4:
                                update.percentMonth4 = val;
                                break;
                            case 5:
                                update.percentMonth5 = val;
                                break;
                            case 6:
                                update.percentMonth6 = val;
                                break;
                            case 7:
                                update.percentMonth7 = val;
                                break;
                            case 8:
                                update.percentMonth8 = val;
                                break;
                            case 9:
                                update.percentMonth9 = val;
                                break;
                            case 10:
                                update.percentMonth10 = val;
                                break;
                            case 11:
                                update.percentMonth11 = val;
                                break;
                        }
                    } else if (type === "amounts") {
                        const val= typeof change.newValue === "string" ? parseFloat(change.newValue) : change.newValue;
                        switch(month) {
                            case 0:
                                update.amountMonth0 = val;
                                break;
                            case 1:
                                update.amountMonth1 = val;
                                break;
                            case 2:
                                update.amountMonth2 = val;
                                break;
                            case 3:
                                update.amountMonth3 = val;
                                break;
                            case 4:
                                update.amountMonth4 = val;
                                break;
                            case 5:
                                update.amountMonth5 = val;
                                break;
                            case 6:
                                update.amountMonth6 = val;
                                break;
                            case 7:
                                update.amountMonth7 = val;
                                break;
                            case 8:
                                update.amountMonth8 = val;
                                break;
                            case 9:
                                update.amountMonth9 = val;
                                break;
                            case 10:
                                update.amountMonth10 = val;
                                break;
                            case 11:
                                update.amountMonth11 = val;
                                break;
                        }
                    }
                }
            }
        }

        setCollectedChanges([]);

        updateOperationalDriver({
            variables: {
                items: Object.values(byId)
            }
        }).then(res => {
            if(res.data?.updateOperationalDriverAssumptions) {
                setLastSaved(new Date());
                return;
            }
            toast.error("Failed to save changes");
        }).catch(() => {
            toast.error("Failed to save changes");
        });
    }, [updateOperationalDriverLoading, collectedChanges]);

    function updateGroupedRowData(change: SimpleHoTChange): void {
        let newVal = typeof change.newValue === "string" ? parseFloat(change.newValue) : change.newValue;
        if(Number.isNaN(newVal)) {
            newVal = null;
        }

        if(change.row.isCompound && change.compoundRowValuesIndex !== undefined) {
            const driver = change.row.extraData?.["opItem"] as GroupedOperationalDriver;
            const type = change.row.values[change.compoundRowValuesIndex]?.["valuesType"] as "amounts" | "percents" | undefined;
            const index = COL_TO_MONTH[change.col];
            if(type !== undefined && index !== undefined) {
                if(type === "amounts") {
                    driver.amounts[index] = newVal;
                } else if(type === "percents") {
                    driver.percents[index] = newVal;
                }
            }
        }
    }

    function handleChange(changes: SimpleHoTChange[]): void {
        for(const change of changes) {
            updateGroupedRowData(change);
        }
        setCollectedChanges([...collectedChanges, ...changes]);
    }

    function getDataProperty(col: number, groupBy: GroupByOptions): string {
        if(groupBy === GroupByOptions.OpItem) {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "property";
            } else {
                return "account";
            }
        } else if(groupBy === GroupByOptions.Property) {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "opItem";
            } else {
                return "account";
            }
        } else {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "opItem";
            } else {
                return "property";
            }
        }
    }

    function getColumnLabel(col: number, groupBy: GroupByOptions): string {
        if(groupBy === GroupByOptions.OpItem) {
            if(col === 0) {
                return "Operational Item";
            } else if(col === 1) {
                return "Property";
            } else {
                return "Account";
            }
        } else if(groupBy === GroupByOptions.Property) {
            if(col === 0) {
                return "Property";
            } else if(col === 1) {
                return "Operational Item";
            } else {
                return "Account";
            }
        } else {
            if(col === 0) {
                return "Account";
            } else if(col === 1) {
                return "Operational Item";
            } else {
                return "Property";
            }
        }
    }

    const months = getMonths(properties?.[0] ?? undefined);
    return (
        <div className={css.operationalTabWrapper}>
            <SimpleHoTWrapperControls>
                <div className={cn(controls.simpleHoTWrapperControls, css.operationalTabControls)}>
                    <div className={css.filters}>
                        <Dropdown
                                selectedItem={groupBy}
                                onSelect={item => setGroupBy(item as GroupByOptions)}
                        >
                            <Field>
                                <Label>Primary Column</Label>
                                <Select>{GroupByLabels[groupBy]}</Select>
                            </Field>
                            <Menu>
                                <Item value={GroupByOptions.OpItem}>{GroupByLabels[GroupByOptions.OpItem]}</Item>
                                <Item value={GroupByOptions.Property}>{GroupByLabels[GroupByOptions.Property]}</Item>
                                <Item value={GroupByOptions.Account}>{GroupByLabels[GroupByOptions.Account]}</Item>
                            </Menu>
                        </Dropdown>
                        <FormField>
                            <FormLabel>Filter Primary Column</FormLabel>
                            <FormInput
                                    value={tmpGroupByFilter ?? ""}
                                    onChange={e => setTmpGroupByFilter(e.target.value ?? undefined)}
                            />
                        </FormField>
                    </div>
                    <div className={css.actions}>
                        <div>
                            <Button size="small" ref={addButtonRef} onClick={() => setAddTooltipVisible(!addTooltipVisible)}>
                                Add Operational Driver
                            </Button>
                        </div>
                        <div className={css.saveStatus}>
                            <VZSaveStatus isSaving={collectedChanges.length > 0 || updateOperationalDriverLoading} lastSaved={lastSaved} />
                        </div>
                    </div>
                </div>
            </SimpleHoTWrapperControls>
            <MemoizedSimpleHoTWrapper
                withControls
                openSectionsPersistOnRender
                dataLoading={loading || props.locksLoading}
                rows={simpleHotRows}
                nestedRows={true}
                columns={[
                    {data: getDataProperty(0, groupBy)},
                    {data: getDataProperty(1, groupBy)},
                    {data: getDataProperty(2, groupBy)},
                    {data: "status"},
                    {data: "type"},
                    {data: "values.0", type: "numeric", allowInvalid: false},
                    {data: "values.1", type: "numeric", allowInvalid: false},
                    {data: "values.2", type: "numeric", allowInvalid: false},
                    {data: "values.3", type: "numeric", allowInvalid: false},
                    {data: "values.4", type: "numeric", allowInvalid: false},
                    {data: "values.5", type: "numeric", allowInvalid: false},
                    {data: "values.6", type: "numeric", allowInvalid: false},
                    {data: "values.7", type: "numeric", allowInvalid: false},
                    {data: "values.8", type: "numeric", allowInvalid: false},
                    {data: "values.9", type: "numeric", allowInvalid: false},
                    {data: "values.10", type: "numeric", allowInvalid: false},
                    {data: "values.11", type: "numeric", allowInvalid: false},
                ]}
                cellStyler={(_row, col, rowData, _cellValue, valuesIndex?: number) => {
                    const style: SimpleHoTCellStyle = {};
                    const isParentRow = rowData.children !== undefined;
                    if([0, 1, 2, 3, 4].includes(col) || isParentRow) {
                        style.isReadOnly = true;
                    }
                    const opItem = rowData?.extraData?.["opItem"] as GroupedOperationalDriver;
                    if(opItem) {
                        if(locksLookup.has(opItem.propertyId)) {
                            style.isReadOnly = true;

                            if(col === 0) {
                                style.cssClasses = ["lockedColumn"];
                            }
                        }

                        if(valuesIndex !== undefined && rowData.isCompound && col >= 5) {
                            const row = rowData as CompoundSimpleHotRow;
                            const type = row.values[valuesIndex]?.["valuesType"] as "amounts" | "percents" | undefined;
                            if(type === "percents") {
                                style.numericFormat = "0,0.00";
                                style.cssClasses = style.cssClasses ===  undefined ? [] : [...style.cssClasses];
                                style.cssClasses.push("percentage-cell");
                            } else if(type === "amounts") {
                                style.numericFormat = "$0,0.00";
                                if(row.extraData?.["amountsNotAvailable"] === true) {
                                    style.isReadOnly = true;
                                }
                            }
                        }
                    } else if(col >= 5) {
                        const childrenLen = rowData.children?.length ?? 0;
                        if(childrenLen > 0) {
                            style.numericFormat = "$0,0.00";
                        }
                    }
                    return style;
                }}
                contextMenu={{
                    "copyToOtherProperties": {
                        name: "Copy To Other Properties/Accounts",
                        hidden: (selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            if (!(selectedRows.size === 1 && selectedCols.size === 1 && isValidCol)) {
                                // Copy is possible when right click on non-assumption data column and single row selected
                                return true;
                            }
                            return false;
                        },
                        disabled: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return true;
                            }
                            const row = selectedRowData[0]?.rowData;
                            const opItem = row?.extraData?.["opItem"] as GroupedOperationalDriver | undefined;
                            if (!opItem) {
                                return true;
                            }
                            return false;
                        },
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return;
                            }
                            const row = selectedRowData[0]?.rowData;
                            const opItem = row?.extraData?.["opItem"] as GroupedOperationalDriver | undefined;
                            if (!opItem) {
                                return;
                            }

                            const matchingUnlockedDriverPropertyIds = rows.filter(driver =>
                                driver.name === opItem.name
                                && driver.operationalMetricTypeId === opItem.operationalMetricTypeId
                                && !locksLookup.has(driver.propertyId)
                            ).map(p => p.propertyId).dedupe();

                            const propertySelectOptions = (properties ?? []).map(p => ({
                                id: p.id,
                                name: p.name,
                                disabled: locksLookup.has(p.id),
                            }));

                            if (propertySelectOptions.every(p => p.disabled === true)) {
                                toast.error("All properties locked for this period");
                                return;
                            }

                            setCopyModal({
                                sourceDriver: {
                                    driverId: opItem.driverId,
                                    name: opItem.name,
                                    propertyId: opItem.propertyId,
                                    propertyName: opItem.propertyName,
                                    destinationAccountId: opItem.destinationAccountId,
                                    accountLabel: opItem.accountLabel,
                                    operationalMetricTypeId: opItem.operationalMetricTypeId,
                                    secondaryOperationalMetricTypeId: opItem.secondaryOperationalMetricTypeId,
                                    status: getStatusText(opItem),
                                    locked: opItem.locked,
                                    valuesLocked: opItem.valuesLocked,
                                    configLocked: opItem.configLocked,
                                    amounts: opItem.amounts,
                                    percents: opItem.percents,
                                    delayRecognition: opItem.delayRecognition,
                                    cumSum: opItem.cumSum,
                                    nonProrated: opItem.nonProrated,
                                    minValue: opItem.minValue,
                                    maxValue: opItem.maxValue,
                                },
                                destinationProperties: propertySelectOptions,
                                defaultSelectedProperties: matchingUnlockedDriverPropertyIds,
                                accounts: accounts.map(a => ({id: a.id, label: `${a.accountNumber} ${a.name}`})),
                            });
                        }
                    },
                    "delete": {
                        name: "Delete Driver(s)",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedDriverIds = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const opItem = child.extraData?.["opItem"] as GroupedOperationalDriver;
                                        if (opItem && !selectedDriverIds.has(opItem.driverId)) {
                                            selectedDriverIds.add(opItem.driverId);
                                        }
                                    }
                                } else {
                                    const opItem = rowData.extraData?.["opItem"] as GroupedOperationalDriver;
                                    if (opItem && !selectedDriverIds.has(opItem.driverId)) {
                                        selectedDriverIds.add(opItem.driverId);
                                    }
                                }
                            }

                            setDeleteModal({
                                driverIds: Array.from(selectedDriverIds)
                            });
                        }
                    },
                    "changeStatus": {
                        name: "Set Non-Admin Access",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const itemIds: string[] = [];
                            const drivers: GroupedOperationalDriver[] = [];
                            const itemNames = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for(const selectedItem of filteredSelection) {
                                const row = selectedItem.rowData;
                                if(row.children && row.children.length > 0) {
                                    for(const child of row.children) {
                                        const opItem = child.extraData?.["opItem"] as GroupedOperationalDriver;
                                        if(opItem) {
                                            itemIds.push(opItem.driverId);
                                            drivers.push(opItem);
                                            itemNames.add(opItem.name);
                                        }
                                    }
                                } else {
                                    const opItem = row.extraData?.["opItem"] as GroupedOperationalDriver;
                                    if(opItem) {
                                        itemIds.push(opItem.driverId);
                                        drivers.push(opItem);
                                        itemNames.add(opItem.name);
                                    }
                                }

                                const allLocked = drivers.length > 0 && drivers.every(d => d.locked);
                                const allValuesLocked = drivers.length > 0 && drivers.every(d => d.valuesLocked);
                                const allconfigLocked = drivers.length > 0 && drivers.every(d => d.configLocked);

                                setSetStatusModal({
                                    driverIds: itemIds,
                                    itemName: Array.from(itemNames),
                                    initialLocked: allLocked,
                                    initialValuesLocked: allValuesLocked,
                                    initialConfigLocked: allconfigLocked,
                                });
                            }
                        }
                    },
                    "viewNote": {
                        name: "View/Update Driver Note(s)",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedLineItemIds = new Set<string>();
                            const selectedLineItemNames = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const opItem = child.extraData?.["opItem"] as GroupedOperationalDriver;
                                        if (opItem) {
                                            selectedLineItemIds.add(opItem.driverId);
                                            selectedLineItemNames.add(opItem.name);
                                        }
                                    }
                                } else {
                                    const opItem = rowData.extraData?.["opItem"] as GroupedOperationalDriver;
                                    if (opItem) {
                                        selectedLineItemIds.add(opItem.driverId);
                                        selectedLineItemNames.add(opItem.name);
                                    }
                                }
                            }

                            const idArray = Array.from(selectedLineItemIds);
                            const nameArray = Array.from(selectedLineItemNames);
                            if (idArray.length === 1) {
                                const firstRowId = idArray[0];
                                const firstRowName = nameArray[0];
    
                                if (firstRowId) {
                                    setEditingNoteLineId(firstRowId);
                                    setEditingNoteLineName(firstRowName ?? "");
                                }
                            } else if (idArray.length > 1) {
                                setBulkEditingNoteLineIds(idArray);
                                setBulkEditingNoteLineNames(nameArray);
                            }
                        }
                    },
                    "viewMinMax": {
                        name: "Edit Configuration",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedDriverConfigItems: Map<string, OpDriverConfigItem> = new Map();
                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const opItem = child.extraData?.["opItem"] as GroupedOperationalDriver;
                                        if (opItem) {
                                            selectedDriverConfigItems.set(
                                                opItem.driverId,
                                                {
                                                    minValue: opItem.minValue,
                                                    maxValue: opItem.maxValue,
                                                    cumSum: opItem.cumSum ?? false,
                                                    nonProrated: opItem.nonProrated,
                                                    propertyId: opItem.propertyId,
                                                    year: opItem.year,
                                                    versionType: opItem.versionType,
                                                    destinationAccountId: opItem.destinationAccountId,
                                                    sourceMetricId: opItem.operationalMetricTypeId,
                                                    name: opItem.name,
                                                }
                                            );
                                        }
                                    }
                                } else {
                                    const opItem = rowData.extraData?.["opItem"] as GroupedOperationalDriver;
                                    if (opItem) {
                                        selectedDriverConfigItems.set(
                                            opItem.driverId,
                                            {
                                                minValue: opItem.minValue,
                                                maxValue: opItem.maxValue,
                                                cumSum: opItem.cumSum ?? false,
                                                nonProrated: opItem.nonProrated,
                                                propertyId: opItem.propertyId,
                                                year: opItem.year,
                                                versionType: opItem.versionType,
                                                destinationAccountId: opItem.destinationAccountId,
                                                sourceMetricId: opItem.operationalMetricTypeId,
                                                name: opItem.name,
                                            }
                                        );
                                    }
                                }
                            }

                            setConfigModal(Array.from(selectedDriverConfigItems.values()));
                        }
                    },
                }}
                hotProps={{
                    height: "50vh",
                    colHeaders: [getColumnLabel(0, groupBy), getColumnLabel(1, groupBy), getColumnLabel(2, groupBy), "Non-Admin Access", "Type", ...months],
                    colWidths: [150, 150, 150, 150, 120, ...(new Array(12).fill(90))],
                    fixedColumnsStart: 5,
                    autoRowSize: false,
                    autoColumnSize: false,
                    rowHeights: 40,
                    undo: false
                }}
                onChange={handleChange}
            />
            <SetStatusModal
                visible={setStatusModal !== null}
                budgetYear={props.budgetYear}
                itemName={setStatusModal?.itemName ?? []}
                itemIds={setStatusModal?.driverIds ?? []}
                itemLabel="Operational Driver"
                itemLabelPlural="Operational Drivers"
                initialLocked={setStatusModal?.initialLocked ?? false}
                initialValuesLocked={setStatusModal?.initialValuesLocked ?? false}
                submitting={setStatusLoading}
                onSubmit={async (permissionLevel) => {
                    const result = await setOperationalDriversStatus({
                        variables: {
                            ids: setStatusModal?.driverIds ?? [],
                            budgetYear: props.budgetYear,
                            // since the permissions cascade right now, they can be set exclusively like this
                            // so that one is true while the others are false. if permissions end up splitting,
                            // like someone can set min/max BUT NOT change assumption values, then this stops working
                            locked: permissionLevel === "editOnly",
                            valuesLocked: permissionLevel === "readOnly",
                            configLocked: permissionLevel === "configureDriver",
                        },
                    });
                    return !!result.data?.setOperationalDriversStatus;
                }}
                onClose={() => setSetStatusModal(null)}
                onStatusSet={() => {
                    setSetStatusModal(null);
                    refetch();
                }}
            />
            <DeleteLineItemsModal
                visible={deleteModal !== null}
                itemIds={deleteModal?.driverIds ?? []}
                itemLabel="Operational Driver"
                itemLabelPlural="Operational Drivers"
                submitting={deleteOperationalDriversLoading}
                onDelete={async () => {
                    const result = await deleteOperationalDrivers({
                        variables: {
                            ids: deleteModal?.driverIds ?? [],
                            budgetYear: props.budgetYear,
                        },
                    });
                    return !!result.data?.deleteOperationalDrivers;
                }}
                onClose={() => setDeleteModal(null)}
                onDeleted={() => {
                    const count = deleteModal?.driverIds.length ?? 0;
                    const label = count === 1 ? "operational driver" : "operational drivers";
                    toast.success(`Deleted ${count} ${label}`);
                    setDeleteModal(null);
                    refetch();
                }}
                onDeleteFailed={() => {
                    toast.error("Failed to delete operational drivers");
                }}
            />
            {copyModal && (
                <CopyOperationalDriverAssumptionsModal
                    visible={copyModal !== null}
                    budgetYear={props.budgetYear}
                    versionType={props.versionType}
                    sourceDriver={copyModal.sourceDriver}
                    destinationProperties={copyModal.destinationProperties}
                    defaultSelectedProperties={copyModal.defaultSelectedProperties}
                    accounts={copyModal.accounts}
                    onClose={() => setCopyModal(null)}
                    onCopied={() => {
                        setCopyModal(null);
                        refetch();
                    }}
                />
            )}
            {addTooltipVisible && addButtonRef.current &&
                <CreateOperationalDriverTooltipModal
                    budgetYear={props.budgetYear}
                    versionType={props.versionType}
                    properties={properties ?? []}
                    locksLookup={locksLookup}
                    operationalMetricTypes={props.operationalMetricTypes}
                    referenceElement={addButtonRef.current}
                    onClose={() => setAddTooltipVisible(false)}
                    onCreated={() => {
                        setAddTooltipVisible(false);
                        refetch();
                    }}
                />
            }

            <OpDriverNoteModal
                lineName={editingNoteLineName}
                lineId={editingNoteLineId}
                onSave={() => setEditingNoteLineId(null)}
                onClose={() => setEditingNoteLineId(null)}
            />

            <OpBulkDriverNoteModal
                lineNames={bulkEditingNoteLineNames}
                lineIds={bulkEditingNoteLineIds}
                onSave={() => setBulkEditingNoteLineIds(null)}
                onClose={() => setBulkEditingNoteLineIds(null)}
            />

            {configModal &&
                <OpDriverConfigModal
                    drivers={configModal}
                    isBusySaving={updateOperationalDriverLoading}
                    onSave={items => {
                        updateOperationalDriver({
                            variables: {
                                items: items,
                            }
                        }).then(res => {
                            if(res.data?.updateOperationalDriverAssumptions) {
                                setLastSaved(new Date());
                                setConfigModal(null);
                                return;
                            }
                            toast.error("Failed to save changes");
                        }).catch(() => {
                            toast.error("Failed to save changes");
                        });
                    }}
                    onClose={() => setConfigModal(null)}
                />
            }
        </div>
    );
}

const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    // Everything but rows is static.
    return prevProps.rows === nextProps.rows;
});
